import { useCallback, useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Alert,
  Box,
  Card,
  CardHeader,
  CircularProgress,
  Dialog,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Portal,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { type Locale } from '@realadvisor/agency-utils';
import { ExternalLink, MoreVertical, Share2, Trash2 } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import { ContactQuickActions } from '@modules/_shared/components/ContactQuickActions';
import { DialogStickyHeader } from '@modules/_shared/components/DialogStickyHeader';
import { Drawer } from '@modules/_shared/components/drawer/Drawer';
import { RaForm } from '@modules/_shared/components/form/RaForm';
import { MutationErrorModal } from '@modules/_shared/components/MutationErrorModal';
import RatingDisplay from '@modules/_shared/components/RatingDisplay';
import { stickyDialogSx } from '@modules/_shared/components/stickyDialogSx';
import { TransactionCard } from '@modules/_shared/components/TransactionCard';
import { TransactionSelect } from '@modules/_shared/components/TransactionSelect';
import { UserCard } from '@modules/_shared/components/UserCard';
import { useNavigateWithSearchParams } from '@modules/_shared/hooks/useNavigateWithSearchParams';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { useAutoTour } from '@modules/product-updates/tours/TourProvider';
import { ReviewAnswerCard } from '@modules/reviews/ReviewAnswerCard';
import {
  DELETE_REVIEW,
  GET_REVIEW_QUERY,
  UPDATE_REVIEW,
} from '@modules/reviews/reviewsQueries';
import { CardButton } from '@modules/visibility-team/MemberDetailsCard';
import { ShareDialog } from '@modules/visibility-team/ShareDialog';
import { useLocale } from '@shared/hooks/locale';

export const REVIEW_DRAWER_FRAGMENT = gql(/* GraphQL */ `
  fragment ReviewDrawer on reviews {
    id
    completed_at
    created_at
    rating
    review_text
    first_name
    last_name
    email_address
    display_name
    is_buyer
    formatted_address
    transaction_date
    owner_answer
    owner_answer_date
    user {
      id
      agent_slug
      ...UserCard_user
      default_team {
        id
        name
      }
    }
    property_transaction {
      id
      ...TransactionCard
    }
  }
`);

type ReviewDrawerProps = {
  reviewId?: string;
  onClose?: () => void;
  transactionsPath?: string;
};

export const ReviewDrawer = ({
  reviewId: reviewIdProp,
  onClose,
  transactionsPath = '/transactions',
}: ReviewDrawerProps = {}) => {
  const { t, dateLocale, locale } = useLocale();
  const { me } = useAppData();
  const { breakpoints } = useTheme();
  const navigate = useNavigateWithSearchParams();
  const { reviewId: routeReviewId } = useParams();
  const reviewId = reviewIdProp ?? routeReviewId;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [updateError, setUpdateError] = useState<null | ErrorLike>(null);
  const [updateReviewTextModalOpened, setUpdateReviewTextModalOpened] =
    useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const { data, error, loading } = useQuery(GET_REVIEW_QUERY, {
    variables: { id: reviewId ?? '' },
    skip: reviewId == null,
  });

  useAutoTour('reviews-reply');

  const [deleteReview] = useMutation(DELETE_REVIEW);
  const [updateReview, { loading: updateReviewLoading }] =
    useMutation(UPDATE_REVIEW);

  const handleClose = useCallback(() => {
    if (onClose != null) {
      onClose();
      return;
    }
    navigate('../');
  }, [navigate, onClose]);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const toLongDateTime = (dateString: string) =>
    new Intl.DateTimeFormat(dateLocale.code, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    }).format(new Date(dateString).getTime());

  const userActions = useMemo(() => {
    if (data?.reviews_by_pk?.user == null) {
      return undefined;
    }

    return (
      <>
        <IconButton
          component={Link}
          title={t('See profile')}
          to={`/users/${data.reviews_by_pk.user.id}`}
          target="_blank"
        >
          <ExternalLink size={20} />
        </IconButton>
        <ContactQuickActions
          phoneNumbers={data.reviews_by_pk.user.phone_numbers}
          emails={data.reviews_by_pk.user.emails}
        />
      </>
    );
  }, [data?.reviews_by_pk?.user, t]);

  const title = useMemo(() => {
    const name = `${data?.reviews_by_pk?.first_name ?? ''} ${
      data?.reviews_by_pk?.last_name ?? ''
    }`;

    return t('Reviews / {{name}}', {
      name: name.trim() === '' ? reviewId ?? '' : name,
    });
  }, [t, data?.reviews_by_pk, reviewId]);

  const drawerActions = useMemo(() => {
    if (!me?.is_admin) {
      return undefined;
    }

    return [
      {
        label: t('Delete'),
        onClick: () => {
          if (
            window.confirm(t('Are you sure you want to delete this review?'))
          ) {
            if (reviewId == null) {
              return;
            }

            // Delete transaction and remove from cache
            deleteReview({
              variables: {
                id: reviewId,
              },
              update: cache => {
                cache.evict({
                  id: `reviews:${reviewId}`,
                });
              },
            }).then(() => {
              // Close drawer
              handleClose();
            });
          }
        },
        icon: <Trash2 size={18} />,
      },
    ];
  }, [me, deleteReview, handleClose, reviewId, t]);

  return (
    <Drawer
      onClose={handleClose}
      title={title}
      maxWidth={breakpoints.values.sm}
      actions={drawerActions}
    >
      {error && (
        <Alert severity="error" sx={{ m: 2 }}>
          <pre>{JSON.stringify(error, null, 2)}</pre>
        </Alert>
      )}
      {loading && (
        <LinearProgress
          sx={theme => ({
            position: 'absolute',
            top: 0,
            width: '100%',
            zIndex: theme.zIndex.drawer + 1,
          })}
        />
      )}
      {data?.reviews_by_pk != null && (
        <Stack
          spacing={2}
          sx={{
            p: 2,
          }}
        >
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t('Agent')}
            </Typography>
            <Box
              component={Card}
              variant="outlined"
              sx={{
                p: 2,
              }}
            >
              {data.reviews_by_pk.user != null ? (
                <UserCard
                  user={data.reviews_by_pk.user}
                  userActions={userActions}
                  condensed={true}
                  secondaryText={data.reviews_by_pk.user.default_team?.name}
                />
              ) : (
                <Typography variant="body1">{t('Anonymous')}</Typography>
              )}
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t('Reviewer')}
            </Typography>
            <Box
              component={Card}
              variant="outlined"
              sx={{
                p: 2,
              }}
            >
              <Typography variant="body1">
                {`${data.reviews_by_pk.first_name} ${data.reviews_by_pk.last_name}`}{' '}
                {`(${data.reviews_by_pk.display_name})`}
              </Typography>
              <Typography variant="body1">
                {data.reviews_by_pk.is_buyer ? t('Buyer') : t('Seller')}
              </Typography>
              <Typography variant="body1">
                {data.reviews_by_pk.formatted_address}
              </Typography>
              {data.reviews_by_pk.transaction_date != null && (
                <Typography variant="body1">
                  {new Intl.DateTimeFormat(locale, {
                    month: 'long',
                    year: 'numeric',
                  }).format(new Date(data.reviews_by_pk.transaction_date))}
                </Typography>
              )}
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t('Review')}
            </Typography>
            <Box
              component={Card}
              variant="outlined"
              sx={{
                p: 2,
              }}
            >
              <Stack
                direction="row"
                sx={{
                  gap: 0.5,
                  alignItems: 'center',
                  mb: 1,
                  typography: 'body1',
                }}
              >
                <RatingDisplay rating={data.reviews_by_pk.rating} readOnly />
              </Stack>

              <Typography
                variant="body1"
                sx={me?.is_admin ? { cursor: 'pointer' } : {}}
                onClick={() => {
                  if (!me?.is_admin) {
                    return;
                  }

                  setUpdateReviewTextModalOpened(true);
                }}
              >
                {data.reviews_by_pk.review_text}
              </Typography>
            </Box>
            <ReviewAnswerCard
              reviewId={data.reviews_by_pk.id}
              ownerAnswer={data.reviews_by_pk.owner_answer ?? null}
              ownerAnswerDate={data.reviews_by_pk.owner_answer_date ?? null}
              canEdit={
                me?.is_admin === true ||
                (data.reviews_by_pk.user != null &&
                  me?.id === data.reviews_by_pk.user.id)
              }
              isOwnReply={
                data.reviews_by_pk.user != null &&
                me?.id === data.reviews_by_pk.user.id
              }
            />
            {data?.reviews_by_pk != null && (
              <Box
                sx={{
                  m: 2,
                }}
              >
                <CardButton
                  xs={12}
                  variant="outlined"
                  fullWidth
                  endIcon={<Share2 />}
                  onClick={() => setShareDialogOpen(true)}
                >
                  {t('Share review')}
                </CardButton>
              </Box>
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t('Transaction')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 1,
                position: 'relative',
                minHeight: updateReviewLoading ? 110 : undefined,
              }}
            >
              {updateReviewLoading ? (
                <CircularProgress
                  disableShrink
                  sx={{
                    zIndex: 1,
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: '-15px !important',
                    marginLeft: '-15px !important',
                  }}
                />
              ) : (
                <>
                  {data.reviews_by_pk.property_transaction == null ? (
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          mb: 1,
                        }}
                      >
                        {t('There is no transaction linked to this review')}
                      </Typography>
                      {me?.is_admin && (
                        <TransactionSelect
                          fullWidth
                          filters={
                            data.reviews_by_pk.user != null
                              ? {
                                  _or: [
                                    {
                                      seller_broker_id: {
                                        _eq: data.reviews_by_pk.user.id,
                                      },
                                    },
                                    {
                                      buyer_broker_id: {
                                        _eq: data.reviews_by_pk.user.id,
                                      },
                                    },
                                  ],
                                }
                              : undefined
                          }
                          onChange={transactionId => {
                            if (reviewId == null || transactionId == null) {
                              return;
                            }

                            updateReview({
                              variables: {
                                id: reviewId,
                                review: {
                                  transaction_id: transactionId,
                                },
                              },
                              onError: setUpdateError,
                            });
                          }}
                        />
                      )}
                    </Box>
                  ) : (
                    <Link
                      to={`${transactionsPath}/${data.reviews_by_pk.property_transaction.id}`}
                      target="_blank"
                      css={{ flex: 1 }}
                    >
                      <TransactionCard
                        transaction={data.reviews_by_pk.property_transaction}
                      />
                    </Link>
                  )}
                </>
              )}

              {data.reviews_by_pk.property_transaction != null &&
                me?.is_admin && (
                  <>
                    <CardHeader
                      action={
                        <IconButton
                          aria-label="settings"
                          onClick={handleOpenMenu}
                        >
                          <MoreVertical size={18} />
                        </IconButton>
                      }
                      sx={{
                        pl: 0,
                        flexShrink: 0,
                        position: 'absolute',
                        right: 0,
                        top: 0,
                      }}
                    />
                    <Menu
                      open={anchorEl != null}
                      onClose={handleCloseMenu}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                      }}
                      anchorEl={anchorEl}
                    >
                      <MenuItem
                        onClick={async () => {
                          handleCloseMenu();

                          if (reviewId == null) {
                            return;
                          }

                          updateReview({
                            variables: {
                              id: reviewId,
                              review: {
                                transaction_id: null,
                              },
                            },
                            onError: setUpdateError,
                          });
                        }}
                      >
                        {t('Remove transaction')}
                      </MenuItem>
                    </Menu>
                    <MutationErrorModal
                      error={updateError}
                      onClose={() => setUpdateError(null)}
                    />
                  </>
                )}
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t('Metadata')}
            </Typography>
            <Box
              component={Card}
              variant="outlined"
              sx={{
                p: 2,
              }}
            >
              <Typography variant="body1">
                {t('Created {{date}}', {
                  date: toLongDateTime(data.reviews_by_pk.created_at),
                })}
              </Typography>
              <Typography variant="body1">
                {t('Completed: {{date}}', {
                  date:
                    data.reviews_by_pk.completed_at != null
                      ? toLongDateTime(data.reviews_by_pk.completed_at)
                      : t('Not yet'),
                })}
              </Typography>
            </Box>
          </Box>

          <Portal>
            <Dialog
              fullWidth
              open={updateReviewTextModalOpened}
              maxWidth="sm"
              onClose={() => setUpdateReviewTextModalOpened(false)}
              sx={stickyDialogSx}
            >
              <DialogStickyHeader
                title={t('Edit review')}
                onClose={() => setUpdateReviewTextModalOpened(false)}
              />
              <RaForm
                formDefinition={({ t }) => [
                  {
                    type: 'text',
                    name: 'review_text',
                    label: t('Review'),
                    rows: 10,
                    multiline: true,
                    required: true,
                    gridProps: { md: 12 },
                  },
                ]}
                defaultValues={{
                  review_text: data.reviews_by_pk.review_text ?? undefined,
                }}
                onCancel={isFormDirty =>
                  isFormDirty === false && setUpdateReviewTextModalOpened(false)
                }
                onSubmit={data => {
                  if (data.review_text == null || reviewId == null) {
                    return Promise.resolve();
                  }

                  return updateReview({
                    variables: {
                      review: {
                        review_text: data.review_text,
                      },
                      id: reviewId,
                    },
                    onCompleted: () => setUpdateReviewTextModalOpened(false),
                  });
                }}
              />
            </Dialog>
          </Portal>
        </Stack>
      )}
      {data?.reviews_by_pk != null && (
        <ShareDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          shareType="review"
          agentSlug={data?.reviews_by_pk?.user?.agent_slug ?? ''}
          tenantId={me?.tenant?.id ?? ''}
          locale={locale as Locale}
          reviewId={reviewId ?? ''}
        />
      )}
    </Drawer>
  );
};
